import React, { useState, useMemo } from 'react';
import { Field, Formik, Form, FieldArray } from 'formik';
import { useSelector, useStore } from 'react-redux';
import {
  keyBy,
  mapValues,
  get,
  pick,
  omit,
  has,
  groupBy,
  values as objectValues,
  uniqBy,
  flatten,
  keys,
  sortBy,
  isNil,
  uniq,
  max,
} from 'lodash';
import hash from 'object-hash';
import PropTypes from 'prop-types';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { MessageBar, MessageBarType, DetailsRow, SelectionMode } from 'office-ui-fabric-react';
import moment from 'moment';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { useBoolean } from '@uifabric/react-hooks';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react';
import StackGrid from '../StackGrid';
import TextField from '../TextField';
import Dropdown from '../Dropdown';
import styles from './RoyaltyFeeForm.style';
import DynamicDetailsList from '../DynamicDetailsList/DynamicDetailsList';
import MultiPickerFormik from '../MultiPicker/MultiPickerFormik';
import TextButton from '../TextButton';
import { pivotWider, rowTransformer } from '../DynamicDetailsList/RowTransformation';
import {
  defaultTransformer,
  defaultPivotWiderTransformer,
  getAllColumnsWithOperators,
} from '../DynamicDetailsList/ContractTransactionColumns';
import theme from '../../theme.style';
import { pxToRem } from '../../utils';
import { effectiveDateColumn, expirationDateColumn } from '../DynamicDetailsList/DateColumns';
import { menuColumn } from '../DynamicDetailsList/generalColumns';
import DatePicker from '../DatePicker';

const createNewRate = (contract, overrides = {}) => ({
  effectiveDate: new Date(contract.effectiveDate),
  terminationDate: new Date(contract.terminationDate),
  type: 'include',
  ...overrides,
});

const calculationParameters = Object({
  percentageRevenue: {
    key: 'percentageRevenue',
    label: '% Revenue',
    props: {
      placeholder: 'Percent',
      inputProps: { min: 0, max: 100 },
    },
    property: 'percentageRevenue',
  },
  percentagePrice: {
    key: 'percentagePrice',
    label: '% Price',
    props: {
      placeholder: 'Percent',
      inputProps: { min: 0, max: 100 },
    },
  },
  unitRate: {
    key: 'unitRate',
    label: 'Unit Rate',
    props: {
      placeholder: 'Rate',
    },
    property: 'unitRate',
  },
});

export const expandRateAssignments = rateAssignments =>
  flatten(
    rateAssignments.map(r => {
      if (!r?.tiers) {
        return [r];
      }
      const tiers = groupBy(r.tiers, 'tierId');
      const calcParams = groupBy(r.calcParams, 'tierId');
      const tierIds = keys(tiers);
      const tieredRateAssignments = tierIds.map(id => ({
        ...r,
        filterId: id + r.filterId,
        _id: id + r.filterId,
        calcParams: calcParams[id],
        tierId: id,
        tieringUpper: tiers[id][0].upper,
      }));

      return sortBy(tieredRateAssignments, 'tieringUpper');
    }),
  );

const RoyaltyFeeForm = ({
  agreement,
  onSubmit,
  onCancel,
  isCreatingAgreement,
  contractAddress,
}) => {
  let calculationMethodDimensions;
  const calculationMethodDimensionOptions = {};
  const store = useStore();
  const allColumns = useMemo(
    () =>
      getAllColumnsWithOperators(true, [
        'meterName',
        'term',
        'offerId',
        'subscriptionGuid',
        'tpId',
        'serviceId',
      ]),
    [],
  );
  const [dialogHidden, { setTrue: hideDialog, setFalse: showDialog }] = useBoolean(true);
  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, null));
  const agreements = useSelector(state =>
    get(state, `entities.contracts.${contractAddress}.agreements`, []).map(agreementAddress =>
      get(state, `entities.agreements.${agreementAddress}`),
    ),
  );
  const isPercap = !!contract.perCapDays;
  const [tierRanges, setTierRanges] = useState(
    get(agreement, 'rateAssignments[0].tiers')?.reduce(
      (acc, next) => ({ ...acc, [next.tierId]: next.upper }),
      {},
    ) || { 1: undefined },
  );
  const contractDateColumns = [
    effectiveDateColumn(contract?.effectiveDate, contract?.terminationDate, 'rates'),
    expirationDateColumn(contract?.effectiveDate, contract?.terminationDate, 'rates'),
  ];

  /**
   * Convert a rateAssignment to formik values which needed to render
   * the "Rate Assignment" rows.
   *
   * @param {Object} str
   * @returns
   */
  const rateAssignmentToFormikValue = str => rateAssignment => {
    const formikRateAssignment = { ...rateAssignment };

    // update calcParams
    rateAssignment.calcParams?.forEach(calcParam => {
      formikRateAssignment[calcParam.property] = calcParam.value;
    });

    // convert dimensions to formik values

    const dimensionsToFormikValues = pivotWider(
      mapValues(keyBy(allColumns, 'key'), 'pivotWiderTransformer'),
      defaultPivotWiderTransformer,
      str,
    );

    const formikDimensionValues = dimensionsToFormikValues(rateAssignment);

    Object.assign(formikRateAssignment, formikDimensionValues);

    return formikRateAssignment;
  };

  /**
   * Convert an agreement from the API to the formik values needed to render
   * the agreement values.
   *
   * @param {Object} agreementInfo
   * @param {Object} contractInfo
   * @param {Object} storeInfo
   * @returns
   */
  const agreementToFormikValues = (agreementInfo, contractInfo, storeInfo) => {
    const initialValues = {
      assignedDimensions: [],
      rates: [createNewRate(contractInfo)], // defaults to creating 1 rate
      royaltyFeeName: '',
      calculationMethod: '',
      useTiering: false,
    };

    if (!agreementInfo) {
      return initialValues;
    }

    initialValues.useTiering = !!agreementInfo.tieringMethod;

    initialValues.royaltyFeeName = agreementInfo.name;

    if (agreementInfo.calculation) {
      initialValues.calculationMethod = agreementInfo.calculation.slug;
    }

    if (agreementInfo.dimensions) {
      initialValues.assignedDimensions = allColumns.filter(column =>
        agreementInfo.dimensions.includes(column.key),
      );
    }

    const rateAssignments = agreementInfo.tieringMethod
      ? expandRateAssignments(agreementInfo.rateAssignments)
      : agreementInfo.rateAssignments;

    if (agreementInfo.rateAssignments) {
      initialValues.rates = rateAssignments.map(rateAssignmentToFormikValue(storeInfo));
    }

    if (initialValues.useTiering) {
      initialValues.tieringMethod = agreementInfo.tieringMethod;
      initialValues.tieringType = agreementInfo.tieringType;
      initialValues.tieringResetInterval = agreementInfo.tieringResetInterval;
      if (initialValues.tieringResetInterval !== 'contractToDate') {
        // we want to convert this date to the date of the first reset, rather than the start date
        // eslint-disable-next-line no-console
        console.log(agreementInfo.tieringResetStartDate);
        initialValues.tieringResetStartDate = moment
          .utc(agreementInfo.tieringResetStartDate, true)
          .add(1, 'year')
          .local(true)
          .toDate();
      }
    }

    return initialValues;
  };
  const initialValues = agreementToFormikValues(agreement, contract, store);

  const calculationMethodOptions = useSelector(state =>
    Object.values(get(state, 'entities.calculationTypes', {})).map(calcType => {
      calculationMethodDimensionOptions[calcType.slug] = calcType.calcParams.map(calcParam => {
        const parameter = calculationParameters[calcParam];
        return {
          key: calcParam,
          name: parameter.label,
          component: TextField,
          property: parameter.property,
          props: {
            type: 'number',
            ...parameter.props,
          },
        };
      });
      return {
        key: calcType.slug,
        text: calcType.name,
      };
    }),
  );

  const tieringMethods = [
    { key: 'netRevenueUnits', text: 'Net Revenue Units' },
    { key: 'netRevenue', text: 'Net Revenue' },
  ];

  const tieringTypes = [{ key: 'standard', text: 'Standard' }];

  const tieringResetIntervals = [
    { key: 'contractToDate', text: 'Contract To Date' },
    { key: 'annual', text: 'Annual' },
  ];

  const tieredRowValidator = rateAssignment => {
    if (rateAssignment.tieringUpper === 0) return '0 is not a valid tiering boundary';
    if (!/\d+/.test(rateAssignment.tierId)) return 'tierId must be numeric';
    return undefined;
  };

  const rowValidator = useTiering => row => {
    // TODO: Add validation for tiering, or pass back from api at least
    const missingFields = [];
    let dateErrors;
    let hasValidCalculationValues = true;
    // validate calculation fields
    calculationMethodDimensions.forEach(column => {
      if (isNil(row[column.key])) {
        missingFields.push(column.name);
      } else if (
        column.props?.inputProps &&
        (row[column.key] < column.props.inputProps.min ||
          row[column.key] > column.props.inputProps.max)
      ) {
        hasValidCalculationValues = false;
      }
    });

    // if any fields has invalid values return
    if (!hasValidCalculationValues) {
      return `Calculation fields must be populated with a valid number. Please edit the highlighted row(s). `;
    }

    // validate that all date fields are there
    contractDateColumns.forEach(column => {
      if (!row[column.key]) {
        missingFields.push(column.name);
      } else if (
        new Date(row[column.key]) > new Date(contract.terminationDate) ||
        new Date(row[column.key]) < new Date(contract.effectiveDate)
      ) {
        dateErrors = `Invalid ${column.name}. Please edit the highlighted row(s).`;
      }
    });

    if (dateErrors) return dateErrors;
    if (missingFields.length > 0) {
      return `Missing Required Fields: ${missingFields.join(
        ', ',
      )}. Please edit the highlighted row(s).`;
    }

    if (useTiering) return tieredRowValidator(row);
    return undefined;
  };

  const formValidator = values => {
    const { useTiering } = values;
    const formErrors = {
      royaltyFeeName: undefined,
      calculationMethod: undefined,
    };

    if (!values.royaltyFeeName) {
      formErrors.royaltyFeeName = 'Fee name is required.';
    } else if (agreements) {
      Object.values(agreements).forEach(someAgreement => {
        if (
          someAgreement &&
          someAgreement.name.toLowerCase() === values.royaltyFeeName &&
          agreement?.address !== someAgreement.address
        ) {
          formErrors.royaltyFeeName = 'Name is already in use.';
        }
      });
    }
    if (!values.calculationMethod) {
      formErrors.calculationMethod = 'Calculation method is required.';
    }

    if (values.rates.length === 0) {
      const filterErrors = ['At least one rate must be assigned.'];

      return { filters: filterErrors, filterErrors, formErrors };
    }
    const calculationFields = calculationMethodDimensions.map(x => x.key);

    const filterTransformer = rowTransformer(
      {
        ...calculationFields,
        ...values.assignedDimensions,
      },
      defaultTransformer,
      'effectiveDate',
      'terminationDate',
      'type',
      ...calculationFields,
    );
    const filtersValidation = values.rates
      .map(f => ({ ...f, dimensions: filterTransformer(f).dimensions }))
      .map(rowValidator(useTiering));
    const filterErrors = filtersValidation.filter(err => err !== undefined);
    if (filtersValidation.some(v => !!v)) {
      return { filters: filtersValidation, filterErrors, formErrors };
    }
    // if there are errors so far, return them
    if (!(filterErrors.length === 0 && !formErrors.royaltyFeeName && !formErrors.calculationMethod))
      return { filters: filtersValidation, filterErrors, formErrors };

    if (useTiering) {
      // should warn if tier ids are non-contiguous
      const [first, ...rest] = uniqBy(
        sortBy(
          values.rates.map(r => ({ ...r, tierId: parseInt(r.tierId, 10) })),
          'tierId',
        ),
        r => r.tierId,
      );
      const error =
        'Tiering Ids are out of order, ensure that the Tier Id and the Tier Thresholds are in the same order.';
      const findGaps = (last, [next, ...tail]) => {
        if (!next) return undefined;
        // next.tieringUpper is max, and last.tieringUpper is defined, so we are in order
        if (!next.tieringUpper && last.tieringUpper) return findGaps(next, tail);
        // last.tieringUpper is defined and smaller than next.tieringUpper
        if (last.tieringUpper && next.tieringUpper > last.tieringUpper) return findGaps(next, tail);
        return error;
      };
      const hasGaps = findGaps(first, rest);
      if (hasGaps) {
        return { filterErrors: [hasGaps], formErrors };
      }
    }

    return {};
  };

  const collapseStructure = rateAssignments => {
    const cleanUpper = upper => (upper === '' ? undefined : upper);
    return {
      ...rateAssignments[0],
      tiers: rateAssignments.reduce(
        ([agg, lower], { tierId, tieringUpper }) => [
          [...agg, { tierId, lower, upper: cleanUpper(tieringUpper) }],
          tieringUpper,
        ],
        [[], 0],
      )[0],
      calcParams: flatten(
        rateAssignments.map(r => r.calcParams.map(p => ({ ...p, tierId: r.tierId }))),
      ),
    };
  };

  const postProcessTiers = rateAssignments => {
    const structures = objectValues(
      groupBy(
        sortBy(rateAssignments, r => r.tieringUpper || Number.MAX_VALUE),
        r => hash(pick(r, 'dimensions', 'effectiveDate', 'expirationDate')),
      ),
    );
    return structures.map(collapseStructure);
  };

  const submitHandler = async values => {
    const dimensions = values.assignedDimensions.map(dimension => {
      return dimension.key;
    });

    const mapCalculationParams = filter => {
      const calcParams = [];
      calculationMethodDimensions.forEach(method => {
        if (!isNil(filter[method.key])) {
          calcParams.push({ property: method.key, value: filter[method.key] });
        }
      });
      return calcParams;
    };
    const rateAssignments = values.rates
      .map(filter => ({
        ...filter,
        type: 'include',
        effectiveDate: moment.utc(filter.effectiveDate).format('YYYY-MM-DD'),
        terminationDate: moment.utc(filter.terminationDate).format('YYYY-MM-DD'),
        calcParams: mapCalculationParams(filter),
      }))
      .map(
        rowTransformer(
          {
            ...values.assignedDimensions,
          },
          defaultTransformer,
          'effectiveDate',
          'terminationDate',
          'royaltyFeeName',
          'calculationMethod',
          'type',
          'calcParams',
          'unitOfMeasure',
          'tierId',
          'tieringUpper',
        ),
      );

    const rateAssignmentsFinal = values.useTiering
      ? postProcessTiers(rateAssignments)
      : rateAssignments;

    const formValues = {
      name: values.royaltyFeeName,
      dimensions,
      rateAssignments: rateAssignmentsFinal,
      calculationMethod: values.calculationMethod,
    };

    if (values.useTiering) {
      formValues.tieringMethod = values.tieringMethod;
      formValues.tieringMethod = values.tieringMethod;
      formValues.tieringType = values.tieringType;
      formValues.tieringResetInterval = values.tieringResetInterval;
      // reset date should not be set for contractToDate
      formValues.tieringResetStartDate =
        // We want to treat this date as the date of the first reset, not the date of the first start
        values.tieringResetInterval === 'contractToDate'
          ? undefined
          : moment(values.tieringResetStartDate).subtract(1, 'year').utc(true).toDate();
    }

    return onSubmit(formValues);
  };
  const checkRates = rates => {
    const [first, ...rest] = sortBy(uniq(rates.map(r => parseInt(r.tierId, 10))));
    const ratesAreContiguous = (last, [next, ...tail]) => {
      if (!next) return true;
      if (next !== last + 1) return false;
      return ratesAreContiguous(next, tail);
    };
    return !ratesAreContiguous(first, rest);
  };
  const showDialogIfNeeded = async values => {
    const nonContiguous = values.useTiering ? checkRates(values.rates) : false;
    if (!nonContiguous) {
      return submitHandler(values);
    }
    return showDialog();
  };

  return (
    <Formik
      validate={formValidator}
      initialValues={initialValues}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={showDialogIfNeeded}
    >
      {({ values, setValues, submitForm, errors }) => {
        const dialogContentProps = {
          type: DialogType.normal,
          title: 'Tier Ids are not contiguous',
          closeButtonAriaLabel: 'Close',
          subText: 'It is recommended to use tier Ids which are contiguous',
        };
        // Increments the given string id
        const getNextTierId = id => (parseInt(id, 10) + 1).toString();
        // Gets the largest id from an array of rates
        const getMaxTierId = rates => {
          // get the largest tiering id if available
          const maxId = max(
            rates.filter(r => /\d+/.test(r.tierId)).map(r => parseInt(r.tierId, 10)),
          );
          return maxId?.toString();
        };
        const getNextTierForRates = rates => {
          const maxId = getMaxTierId(rates);
          return maxId ? getNextTierId(maxId) : undefined;
        };

        const isTiered = !!values.useTiering;
        const hasTieringReset = isTiered && values.tieringResetInterval !== 'contractToDate';
        const onRenderRow = props => {
          const customStyle = {
            root: {
              backgroundColor: theme.palette.neutralWhite,
              selectors: {
                '& .ms-DetailsRow-fields .ms-DetailsRow-cell': {
                  padding: `${pxToRem(8)} ${pxToRem(4)}`,
                },
                '& .ms-Dropdown-container > .ms-Stack': {
                  marginBottom: `${pxToRem(0)} !important`,
                },
              },
            },
          };
          // eslint-disable-next-line react/prop-types
          if (errors.filters && errors.filters[props.itemIndex])
            customStyle.root = {
              backgroundColor: theme.palette.accentErrorOpaque,
              ':active': { backgroundColor: theme.palette.accentErrorOpaque },
              ':hover': { backgroundColor: theme.palette.accentErrorOpaque },
            };
          return <DetailsRow {...props} styles={customStyle} />;
        };
        // Updates the selected rows tier Id as well as the tieringUpper
        const updateId = (index, id, originalValues, arrayHelpers) => {
          const tieringUpper = has(tierRanges, id) ? tierRanges[id] : originalValues.tieringUpper;
          const newValues = {
            ...originalValues,
            tierId: id,
            tieringUpper,
          };
          arrayHelpers.replace(index, newValues);
        };
        const updateTierRange = (tierId, tieringUpper, arrayHelpers) => {
          const floatTieringUpper = tieringUpper ? parseFloat(tieringUpper) : undefined;
          values.rates
            .map((rate, index) => ({ index, rate }))
            .filter(({ rate }) => tierId === rate.tierId)
            .forEach(({ index, rate }) =>
              arrayHelpers.replace(index, {
                ...rate,
                tieringUpper: floatTieringUpper,
              }),
            );
          if (tierRanges[tierId] !== floatTieringUpper) {
            setTierRanges({ ...tierRanges, [tierId]: floatTieringUpper });
          }
        };
        const tieringColumns = [
          {
            name: 'Tier',
            property: 'tierId',
            key: 'tierId',
            minWidth: 100,
            maxWidth: 100,
            getRenderer:
              (arrayHelpers, { values: originalValues, nameHelper }) =>
              (item, rowIndex, column) =>
                (
                  <Field
                    name={nameHelper(rowIndex, column)}
                    onBlur={({ target }) => {
                      updateId(rowIndex, target.value, originalValues[rowIndex], arrayHelpers);
                    }}
                    component={TextField}
                  />
                ),
          },
          {
            name: 'Threshold',
            property: 'tieringUpper',
            key: 'tieringUpper',
            minWidth: 100,
            maxWidth: 100,
            getRenderer:
              (arrayHelpers, { values: originalValues, nameHelper }) =>
              (item, rowIndex, column) =>
                (
                  <Field
                    name={nameHelper(rowIndex, column)}
                    onBlur={({ target }) => {
                      updateTierRange(originalValues[rowIndex].tierId, target.value, arrayHelpers);
                    }}
                    component={TextField}
                    type="number"
                    placeholder="max"
                  />
                ),
          },
        ];

        return (
          <div>
            {errors.filterErrors && errors.filterErrors.length > 0 && (
              <MessageBar
                className={styles.errorBar}
                isMultiline
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
              >
                Cannot save filters:
                {errors.filterErrors.map(error => {
                  return <li key={error.message}>{error}</li>;
                })}
              </MessageBar>
            )}

            <Form className={styles.royaltyFeeForm} data-testid="royaltyFeeForm">
              <div>
                <StackGrid smCols={1} mdCols={3} lgCols={3} shrink>
                  <Field
                    name="royaltyFeeName"
                    label="Fee Name"
                    component={TextField}
                    errorMessage={errors.formErrors?.royaltyFeeName}
                    required
                    data-testid="fee-name"
                  />

                  <Field
                    smCols={1}
                    mdCols={3}
                    lgCols={3}
                    shrink
                    name="calculationMethod"
                    label="Calculation Method"
                    options={calculationMethodOptions}
                    component={Dropdown}
                    errorMessage={errors.formErrors?.calculationMethod}
                    required
                    data-testid="calculation-method"
                  />

                  {window.config.enableTiering && (
                    <Field
                      name="useTiering"
                      label="Use Tiering"
                      options={[
                        { key: false, text: 'No' },
                        { key: true, text: 'Yes' },
                      ]}
                      component={Dropdown}
                      data-testid="useTiering"
                      onChange={(e, { key }) => {
                        // use tiering
                        if (key) {
                          setValues({
                            ...values,
                            useTiering: true,
                            tieringMethod: undefined,
                            tieringType: 'standard',
                            tieringResetInterval: 'contractToDate',
                            tieringResetStartDate: undefined,
                            rates: values.rates.map((a, i) => ({
                              ...a,
                              tierId: (i + 1).toString(),
                            })),
                          });
                          // don't use tiering
                        } else {
                          setValues({
                            ...values,
                            useTiering: false,
                            tieringMethod: undefined,
                            tieringType: undefined,
                            tieringResetInterval: undefined,
                            tieringResetStartDate: undefined,
                            rates: values.rates.map(a => omit(['tierId', 'tieringUpper'], a)),
                          });
                        }
                      }}
                      disabled={!window.config.enablePercapTiering && isPercap}
                    />
                  )}
                </StackGrid>
                {isTiered && (
                  <StackGrid smCols={1} mdCols={4} lgCols={4} shrink style={{ marginTop: '1.5em' }}>
                    <Field
                      name="tieringMethod"
                      label="Tiering Method"
                      component={Dropdown}
                      options={tieringMethods}
                      errorMessage={errors.formErrors?.tieringMethod}
                      required
                      data-testid="tiering-method"
                    />
                    <Field
                      name="tieringType"
                      label="Tiering Type"
                      component={Dropdown}
                      options={tieringTypes}
                      errorMessage={errors.formErrors?.tieringType}
                      required
                      data-testid="tiering-type"
                    />
                    <Field
                      name="tieringResetInterval"
                      label="Tiering Reset Interval"
                      component={Dropdown}
                      options={tieringResetIntervals}
                      errorMessage={errors.formErrors?.tieringResetInterval}
                      required
                      data-testid="tiering-reset-interval"
                    />
                    {hasTieringReset && (
                      <Field
                        name="tieringResetStartDate"
                        label="Interval Start Date"
                        style={{ marginTop: -6 }}
                        component={DatePicker}
                        required
                        errorMessage={errors.formErrors?.tieringResetStartDate}
                        data-testid="tiering-reset-start-date"
                      />
                    )}
                  </StackGrid>
                )}
              </div>
              <div className={styles.assignmentDimensions}>
                <div className={styles.dimensionsContainer}>
                  <Field
                    label="Assignment Dimensions"
                    name="assignedDimensions"
                    multiSelect
                    component={MultiPickerFormik}
                    options={allColumns}
                    className={styles.dimensionsSelector}
                    multiTag
                    data-testid="assigned-dimensions"
                  />
                </div>

                <FieldArray name="rates">
                  {arrayHelpers => {
                    calculationMethodDimensions = values.calculationMethod
                      ? calculationMethodDimensionOptions[values.calculationMethod]
                      : [];

                    const columns = [
                      ...(isTiered ? tieringColumns : []),
                      ...calculationMethodDimensions,
                      ...values.assignedDimensions,
                      ...contractDateColumns,
                      menuColumn(item => ({
                        ...item,
                        tierId: values.useTiering ? getNextTierId(item.tierId) : undefined,
                      })),
                    ];

                    return (
                      <>
                        {values.rates.length > 0 && (
                          <Field
                            name="rates"
                            component={DynamicDetailsList}
                            columns={columns}
                            arrayHelpers={arrayHelpers}
                            selectionMode={SelectionMode.multiple}
                            ariaLabelForSelectAllCheckbox="rates column"
                            checkButtonAriaLabel="rates"
                            enableRowReorder
                            onRenderRow={onRenderRow}
                            errorMessage="one rate is required"
                          />
                        )}
                        <div className={styles.buttonContainer}>
                          <TextButton
                            type="button"
                            data-testid="add-rate-link"
                            onClick={() => {
                              arrayHelpers.push(
                                createNewRate(
                                  contract,
                                  values.useTiering
                                    ? { tierId: getNextTierForRates(values.rates) }
                                    : {},
                                ),
                              );
                            }}
                          >
                            Add Rate Assignment
                          </TextButton>
                        </div>
                      </>
                    );
                  }}
                </FieldArray>
              </div>

              <div className={styles.formActions}>
                <DefaultButton
                  className={styles.cancelButton}
                  onClick={onCancel}
                  disabled={isCreatingAgreement}
                  data-testid="cancel-button"
                >
                  Cancel
                </DefaultButton>
                <PrimaryButton
                  disabled={isCreatingAgreement}
                  onClick={submitForm}
                  data-testid="save-fee-button"
                >
                  Save and Continue
                  {isCreatingAgreement && (
                    <Spinner
                      data-testid="create-agreement-spinner"
                      size={SpinnerSize.medium}
                      className={styles.loadingSpinner}
                    />
                  )}
                </PrimaryButton>
              </div>
            </Form>
            <Dialog
              hidden={dialogHidden}
              onDismiss={hideDialog}
              dialogContentProps={dialogContentProps}
            >
              <DialogFooter>
                <PrimaryButton onClick={() => submitHandler(values)} text="Submit Anyway" />
                <DefaultButton onClick={hideDialog} text="Cancel" />
              </DialogFooter>
            </Dialog>
          </div>
        );
      }}
    </Formik>
  );
};

RoyaltyFeeForm.propTypes = {
  agreement: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isCreatingAgreement: PropTypes.bool,
  contractAddress: PropTypes.string.isRequired,
};

RoyaltyFeeForm.defaultProps = {
  isCreatingAgreement: false,
  agreement: null,
};

export default RoyaltyFeeForm;
